import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { apiErr } from "@/lib/api";
import { formatWebinarTime } from "@/lib/format";
import { toast } from "sonner";
import { Calendar, Clock, Video, User } from "lucide-react";

const COUNTRIES = [["IN", "India"], ["US", "United States"], ["AE", "UAE"], ["GB", "United Kingdom"], ["DEFAULT", "Other"]];

export default function WebinarRegister() {
  const { instanceId } = useParams();
  const { user, timezone, location } = useApp();
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", country: "IN", city: "", timezone, marketing_consent: true,
  });

  useEffect(() => {
    api.webinarInstance(instanceId).then(setWebinar).catch(() => setWebinar(false));
  }, [instanceId]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      name: user?.name || f.name,
      email: user?.email || f.email,
      mobile: user?.phone || f.mobile,
      country: location?.country && location.country !== "DEFAULT" ? location.country : f.country,
    }));
  }, [user, location]);

  if (webinar === false) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Webinar session not found.</div>;
  if (!webinar) return <div className="max-w-2xl mx-auto px-6 py-32 text-center text-slate-500">Loading…</div>;

  const t = formatWebinarTime(webinar.start_utc, timezone);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.registerWebinar({
        instance_id: instanceId, ...form, industry: webinar.topic,
      });
      toast.success("You're registered!");
      navigate("/webinar/registered", { state: { registration: res.registration, webinar: res.webinar } });
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  const inp = (key, props) => (
    <input data-testid={`reg-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      className="w-full rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange" {...props} />
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10" data-testid="webinar-register">
      {/* your webinar */}
      <div>
        <div className="asf-overline mb-3">Your webinar</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{webinar.title}</h1>
        <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 mt-6 space-y-3 text-sm">
          <div className="flex items-center gap-3"><Calendar className="h-5 w-5 text-asf-orange" /> {t.dayLabel}</div>
          <div className="flex items-center gap-3"><Clock className="h-5 w-5 text-asf-orange" /> {t.time} {t.tzShort} · your local time</div>
          <div className="flex items-center gap-3"><Clock className="h-5 w-5 text-asf-orange" /> {webinar.duration_min} minutes</div>
          <div className="flex items-center gap-3"><Video className="h-5 w-5 text-asf-orange" /> {webinar.location}</div>
          <div className="flex items-center gap-3"><User className="h-5 w-5 text-asf-orange" /> Host: {webinar.host}</div>
        </div>
      </div>

      {/* form */}
      <div className="asf-card !translate-y-0 hover:!translate-y-0 p-8">
        <h2 className="font-display text-xl font-semibold mb-5">Reserve your seat</h2>
        <form onSubmit={submit} className="space-y-4">
          {inp("name", { placeholder: "Full name", required: true })}
          {inp("email", { placeholder: "Email", type: "email", required: true })}
          {inp("mobile", { placeholder: "Mobile number", required: true })}
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="reg-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange">
              {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {inp("city", { placeholder: "City" })}
          </div>
          {inp("timezone", { placeholder: "Timezone", required: true })}
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input data-testid="reg-consent" type="checkbox" checked={form.marketing_consent}
              onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-1 accent-[#FF5A00]" />
            Send me reminders and the exclusive webinar offer via email/WhatsApp.
          </label>
          <button data-testid="reg-submit" disabled={loading} className="btn-primary w-full py-4">
            {loading ? "Reserving…" : "Reserve My Seat"}
          </button>
        </form>
      </div>
    </div>
  );
}
